import React, { FC, memo, useState } from 'react';
import { IActionItem } from '@components/ActionsMenu/types';
import { ContextMenu } from '@components/MainStage/ContextMenu/ContextMenu';
import { DeleteNodeModal } from '@components/MainStage/ContextMenu/NodeContextMenu/DeleteNodeModal';
import RelationshipsMenu from '@components/MainStage/ContextMenu/RelationshipsMenu';
import useDeleteKey from '@components/MainStage/hooks/useDeleteKey';
import useIsNodeAddedToAsset from '@components/MainStage/hooks/useIsNodeAddedToAsset';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { ContextMenuProps } from '@components/MainStage/types';
import { useProject } from '@context/Project/ProjectProvider';
import useCanEditModel from '@hooks/useCanEditModel';
import useToggle from '@hooks/useToggle';
import { NodeDTO, NodeDTOTypes } from '@store/services/nodes/types';
import { themePalette } from '@theme/muiTheme';
import {
  CheckmarkCircleIcon,
  CloseIcon,
  RelationshipsIcon,
  ReportIcon,
  TrashIcon,
} from '@utils/iconsDefs';
import { isSetValidationStatusVisible } from '@utils/validationStatusEligibility';
import { JustificationReportModal } from '@views/ReportsModel/components/JustificationReport/JustificationReportModal';

import { ValidationStatusMenu } from './ValidationStatusMenu';

const NodeContextMenu: FC<ContextMenuProps> = ({ isOpen, onClose, menu }) => {
  const { getNodeById } = useStore();
  const [isDeleteOpen, deleteControls] = useToggle<null | string>();
  const [isJustificationReportOpen, justificationReportControls] = useToggle<
    null | boolean
  >();
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const canDelete = useCanEditModel();

  const nodeId = deleteId || menu?.id || '';
  const isNodeAdded = useIsNodeAddedToAsset(nodeId);
  const node = getNodeById(nodeId);
  const isNodeWithJustificationReport =
    node?.data?.dto?.type === NodeDTOTypes.BusinessAttribute ||
    node?.data?.dto?.type === NodeDTOTypes.SecurityAttribute;

  const {
    toolbox: { mode },
  } = useProject();

  const { selectedIds } = getMultiSelectionState();
  const targetIds =
    selectedIds.length > 1 && selectedIds.includes(nodeId)
      ? selectedIds
      : [nodeId];
  const targetNodes = targetIds
    .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
    .filter((dto): dto is NodeDTO => Boolean(dto));
  const showValidationStatus = mode
    ? isSetValidationStatusVisible(targetNodes, mode)
    : false;

  const handleOpenDeleteModal = () => {
    deleteControls.on();
    onClose();
    setDeleteId(null);
  };

  const handleOpenJustificationReportModal = () => {
    justificationReportControls.on();
    onClose();
  };

  const relationsMenuItem: IActionItem = {
    icon: <RelationshipsIcon fill="currentColor" />,
    text: 'Relationships',
    subComponent: <RelationshipsMenu nodeId={nodeId} onCloseMenu={onClose} />,
    popoverProps: {
      sx: {
        '& .MuiPopover-paper': { borderRadius: 2 },
        '.MuiPaper-root': {
          width: 320,
          maxHeight: 500,
          overflow: 'hidden',
          display: 'flex',
        },
      },
    },
  };

  const validationStatusMenuItem: IActionItem = {
    icon: <CheckmarkCircleIcon fill="currentColor" />,
    text: 'Set validation status',
    subComponent: (
      <ValidationStatusMenu nodeIds={targetIds} onCloseMenu={onClose} />
    ),
  };

  const contextMenuItems: IActionItem[] = [
    relationsMenuItem,

    ...(showValidationStatus ? [validationStatusMenuItem] : []),

    ...(isNodeWithJustificationReport
      ? [
          {
            icon: <ReportIcon fill="currentColor" />,
            text: 'Reports',
            subItems: [
              {
                text: 'Justification statement',
                onClick: handleOpenJustificationReportModal,
              },
            ],
          },
        ]
      : []),

    ...(canDelete
      ? [
          {
            icon: <TrashIcon />,
            text: 'Delete',
            divider: isNodeWithJustificationReport,
            disableHoverColor: true,
            color: themePalette.system.error,
            onClick: handleOpenDeleteModal,
          },
        ]
      : []),
  ];

  const removeMenuItems = [
    relationsMenuItem,
    {
      icon: <CloseIcon fill={themePalette.grey[1000]} />,
      text: 'Remove entity',
      onClick: handleOpenDeleteModal,
      textColor: 'grey.1000',
    },
  ];

  useDeleteKey('node', (id) => {
    if (!canDelete) return;

    setDeleteId(id);
    deleteControls.on();
  });

  if (contextMenuItems.length === 0) return null;

  const menuItems = isNodeAdded ? removeMenuItems : contextMenuItems;

  return (
    <>
      <DeleteNodeModal
        open={isDeleteOpen}
        onClose={deleteControls.off}
        nodeId={nodeId}
      />

      {isNodeWithJustificationReport && (
        <JustificationReportModal
          open={isJustificationReportOpen}
          onClose={justificationReportControls.off}
          node={node}
        />
      )}

      {isOpen && menu && menu.type === 'node' && (
        <ContextMenu
          menuItems={menuItems}
          {...menu}
          top={menu.top - menuItems.length * 40}
        />
      )}
    </>
  );
};

export default memo(NodeContextMenu);
